/**
 * <cart-drawer>
 *
 * The site's ONLY cart interface. It slides in from the right, and it is what
 * both header cart links and the product page's buy button open.
 *
 * ─── The contract this discharges ───────────────────────────────────────────
 *
 * assets/product-form.js was written against a drawer that did not exist yet. On
 * a successful add it dispatches `cart:item-added` on `document`, then falls back
 * to navigating to /cart ONLY IF nothing on the page has registered a
 * <cart-drawer>. Defining this element is therefore the whole integration — the
 * redirect stops on its own and product-form.js needs no edit. Note the check
 * there is `document.querySelector('cart-drawer')`, an element in the DOM, not
 * `customElements.get(...)`, which is why the section lives in the header group
 * and renders on every page.
 *
 * ─── What is NOT hand-rolled ────────────────────────────────────────────────
 *
 * The markup is a real <dialog> opened with showModal(), the same decision
 * assets/site-header.js documents for the mobile nav drawer. Four things come
 * from the platform and are deliberately absent below:
 *
 *   - the focus trap, and returning focus to whatever opened the drawer
 *   - Esc to dismiss
 *   - inertness of the page behind
 *   - page scroll locking — the `scroll-lock` attribute in the markup, matched by
 *     `html:has(dialog[scroll-lock][open]) { overflow: hidden }` in critical.css
 *
 * WHY THE PANEL SLIDES AND THE DIALOG DOES NOT. A dialog goes from display:none
 * to display:block, and a transform cannot transition across a display change
 * without @starting-style gymnastics. So the dialog opens instantly and a child
 * panel is transitioned, toggled on the second frame — the element has to have
 * been laid out at translateX(100%) before the end state is applied, or there is
 * nothing to interpolate from and it snaps.
 *
 * ─── Re-rendering ───────────────────────────────────────────────────────────
 *
 * Every cart mutation asks the SERVER for new HTML rather than building markup
 * here. `money` is a per-shop, per-currency setting and variant swatch resolution
 * already lives in snippets/swatch-source.liquid; reimplementing either in JS
 * would drift from what Liquid renders. /cart/change.js supports bundled section
 * rendering, so the mutation and the re-render are one round trip.
 *
 * 🚨 ONLY [data-cart-content] IS REPLACED. Replacing this element or the <dialog>
 * would tear the open modal out of the top layer mid-interaction and drop the
 * focus trap. The dialog, the panel, the title row and the close button all
 * survive every refresh.
 *
 * Expected markup — see sections/section-cart-drawer.liquid:
 *
 *   <cart-drawer data-section-id="…">
 *     <dialog scroll-lock data-cart-dialog>
 *       <div data-cart-panel>
 *         <p data-cart-title>            <button data-cart-close>
 *         <div data-cart-content>        ← the only thing swapped
 *           <div data-cart-items>
 *             <div data-cart-line data-line="1">
 *               <button data-cart-decrease> <button data-cart-increase>
 *           <div data-cart-footer>
 *           <span hidden data-cart-count-label>
 */

if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      /* Covers reduced motion, where `motion-safe:` means there is no transition
         and transitionend never fires at all, and a transition cut short by the
         tab being backgrounded.

         🚨 THIS MUST STAY LONGER THAN THE PANEL'S TRANSITION. It is a backstop, not
         the normal path — if it fires first it closes the dialog mid-slide and the
         drawer vanishes instead of leaving. The panel is `motion-safe:duration-500`,
         so this is 700. Change one and change the other. */
      static CLOSE_FALLBACK_MS = 700;

      /* Tailwind v4 writes translate utilities to the standalone `translate`
         property, not to `transform` — so a transitionend guarded on 'transform'
         alone never matches and every close silently falls through to the timer
         above. Both are listed because `transition-transform` covers all four of
         transform/translate/scale/rotate, and which one fires depends on how the
         utility is authored. */
      static SLIDE_PROPERTIES = ['translate', 'transform'];

      connectedCallback() {
        this.dialog = this.querySelector('[data-cart-dialog]');
        this.panel = this.querySelector('[data-cart-panel]');
        this.overlay = this.querySelector('[data-cart-overlay]');
        this.content = this.querySelector('[data-cart-content]');
        this.title = this.querySelector('[data-cart-title]');
        this.closeButton = this.querySelector('[data-cart-close]');

        if (!this.dialog || !this.panel || !this.content) {
          return;
        }

        this.sectionId = this.dataset.sectionId;
        this.busy = false;
        this.closeTimer = null;

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleItemAdded = this.handleItemAdded.bind(this);
        this.handleQuantityClick = this.handleQuantityClick.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleBackdropClick = this.handleBackdropClick.bind(this);

        /* Delegated from document, not bound to the links themselves: the header
           renders in a different section, and binding at connect time would miss
           any trigger added later. */
        document.addEventListener('click', this.handleTriggerClick);
        document.addEventListener('cart:item-added', this.handleItemAdded);

        /* Delegated too, and for a stronger reason — every stepper button inside
           [data-cart-content] is destroyed and rebuilt on each refresh, so a
           direct listener would survive exactly one interaction. */
        this.addEventListener('click', this.handleQuantityClick);

        this.closeButton?.addEventListener('click', this.handleClose);
        this.dialog.addEventListener('cancel', this.handleCancel);
        this.dialog.addEventListener('close', this.handleDialogClose);
        this.dialog.addEventListener('click', this.handleBackdropClick);

        /* On /cart the drawer IS the cart page. The stock section is left in the
           markup as the scripting-off fallback and hidden the moment we know
           scripting is on. */
        if (this.hasAttribute('data-open-on-load')) {
          document.querySelectorAll('[data-cart-page-fallback]').forEach((node) => {
            node.hidden = true;
          });
          this.open();
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.handleTriggerClick);
        document.removeEventListener('cart:item-added', this.handleItemAdded);
        this.removeEventListener('click', this.handleQuantityClick);

        this.closeButton?.removeEventListener('click', this.handleClose);
        this.dialog?.removeEventListener('cancel', this.handleCancel);
        this.dialog?.removeEventListener('close', this.handleDialogClose);
        this.dialog?.removeEventListener('click', this.handleBackdropClick);

        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
      }

      /* ── Opening and closing ───────────────────────────────────────────────── */

      /**
       * The header cart links stay real anchors to /cart so they work with
       * scripting off, exactly like the header's search trigger. This intercepts
       * them. Modified clicks are left alone — a middle click or cmd-click is a
       * request for a new tab, and swallowing it would be hostile.
       */
      handleTriggerClick(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const trigger = event.target.closest('[data-cart-toggle]');
        if (!trigger) return;

        event.preventDefault();
        this.open();
      }

      /** The product form's handshake. Refresh first so the drawer never opens on a stale cart. */
      async handleItemAdded() {
        await this.refresh();
        this.open();
      }

      open() {
        if (this.dialog.open) return;

        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }

        this.dialog.showModal();

        /* Two frames, not one. The first gets the panel laid out at
           translateX(100%); the second applies the end state. A single rAF is
           enough in most engines and not in all, and the failure mode is a drawer
           that snaps rather than slides — invisible in code review. */
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.panel.dataset.open = '';
            if (this.overlay) this.overlay.dataset.open = '';
          });
        });
      }

      /**
       * Runs the slide-out, then closes the dialog. Closing immediately would
       * remove it from the layout mid-transition and the animation would never be
       * seen.
       */
      handleClose() {
        if (!this.dialog.open) return;

        delete this.panel.dataset.open;
        if (this.overlay) delete this.overlay.dataset.open;

        const finish = () => {
          if (this.closeTimer) {
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
          }
          this.panel.removeEventListener('transitionend', onEnd);

          if (this.dialog.open) {
            this.dialog.close();
          }
        };

        /* Guarded on propertyName so a future opacity or colour transition on the
           same element cannot close the drawer early — and on the right names, see
           SLIDE_PROPERTIES. */
        const onEnd = (event) => {
          if (event.target === this.panel && CartDrawer.SLIDE_PROPERTIES.includes(event.propertyName)) {
            finish();
          }
        };

        /* With reduced motion `motion-safe:` leaves no transition at all, so
           transitionend never fires and the drawer would sit fully open for the
           whole fallback before blinking out. Asking the element what its duration
           actually is turns that into the instant close it should be, and it also
           covers a merchant or future edit changing the duration without updating
           the constant. */
        const duration = parseFloat(getComputedStyle(this.panel).transitionDuration) || 0;
        if (duration === 0) {
          finish();
          return;
        }

        this.panel.addEventListener('transitionend', onEnd);
        this.closeTimer = setTimeout(finish, CartDrawer.CLOSE_FALLBACK_MS);
      }

      /** Esc. Cancelled so the close runs through the animation like any other. */
      handleCancel(event) {
        event.preventDefault();
        this.handleClose();
      }

      /** Fires however the dialog closed, including paths that bypass handleClose. */
      handleDialogClose() {
        delete this.panel.dataset.open;
        if (this.overlay) delete this.overlay.dataset.open;
      }

      /**
       * Dismiss on a click outside the drawer.
       *
       * Tested by CONTAINMENT against the panel, not by identity against the
       * dialog. The dimming layer covers the dialog's whole box, so the dialog
       * itself is never the click target any more — the identity test this used to
       * do silently stopped dismissing the moment the overlay was added. Same
       * reasoning, and the same fix, as assets/video-list.js.
       */
      handleBackdropClick(event) {
        if (!this.panel.contains(event.target)) {
          this.handleClose();
        }
      }

      /* ── Quantity ──────────────────────────────────────────────────────────── */

      handleQuantityClick(event) {
        const decrease = event.target.closest('[data-cart-decrease]');
        const increase = event.target.closest('[data-cart-increase]');
        const button = decrease || increase;
        if (!button) return;

        /* One mutation at a time. /cart/change.js addresses lines by INDEX, so a
           second request sent before the first returns would be numbered against
           a cart that no longer exists — and if the first removed a line, it would
           silently change the wrong product. */
        if (this.busy) return;

        const row = button.closest('[data-cart-line]');
        if (!row) return;

        const line = Number(row.dataset.line);
        const current = Number(row.dataset.quantity);
        if (!Number.isFinite(line) || !Number.isFinite(current)) return;

        /* Decrementing past 1 means quantity 0, which is how the Cart API removes
           a line. There is no separate remove control by design. */
        const quantity = increase ? current + 1 : current - 1;

        this.changeLine(line, Math.max(0, quantity), button);
      }

      async changeLine(line, quantity, button) {
        this.setBusy(true, button);

        /* Remember where focus was so it can be put back after the swap. Without
           this, changing a quantity drops focus to <body> and a keyboard user is
           thrown out of the drawer on every click. */
        const focus = {
          line,
          control: button?.hasAttribute('data-cart-increase') ? 'increase' : 'decrease',
        };

        try {
          const response = await fetch(`${CartDrawer.root()}cart/change.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              line,
              quantity,
              /* Bundled section rendering: the mutation and the re-render in one
                 round trip. */
              sections: this.sectionId,
            }),
          });

          const data = await response.json();
          if (!response.ok) return;

          /* Sections are rendered after the mutation, and a section that fails to
             render comes back as null in an otherwise successful response — so
             this has to be checked rather than assumed. */
          const html = data.sections?.[this.sectionId];

          if (html) {
            this.render(html, focus);
          } else {
            await this.refresh(focus);
          }
        } catch {
          /* Network failure. The cart is unchanged on the server, so re-rendering
             from it puts the controls back in step with reality. */
          await this.refresh(focus);
        } finally {
          this.setBusy(false, button);
        }
      }

      /**
       * Blocks further clicks while a mutation is in flight. Deliberately no
       * optimistic quantity update: inventory limits can make the server reject a
       * change, and showing a number the cart does not hold is worse than a
       * moment's delay.
       */
      setBusy(state, button) {
        this.busy = state;
        this.content.toggleAttribute('data-cart-busy', state);

        if (state) {
          button?.setAttribute('aria-busy', 'true');
        } else {
          button?.removeAttribute('aria-busy');
        }
      }

      /* ── Rendering ─────────────────────────────────────────────────────────── */

      /** Pulls the section fresh. Used when bundled rendering is unavailable or failed. */
      async refresh(focus) {
        if (!this.sectionId) return;

        try {
          const url = `${CartDrawer.root()}?section_id=${encodeURIComponent(this.sectionId)}`;
          const response = await fetch(url);
          if (!response.ok) return;

          this.render(await response.text(), focus);
        } catch {
          /* Leave the drawer showing what it has. It is stale rather than wrong,
             and the next interaction will try again. */
        }
      }

      render(html, focus) {
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const content = doc.querySelector('[data-cart-content]');
        if (!content) return;

        this.content.innerHTML = content.innerHTML;

        const title = doc.querySelector('[data-cart-title]');
        if (title && this.title) {
          this.title.textContent = title.textContent;
        }

        this.updateHeaderCount(doc);
        this.restoreFocus(focus);
      }

      /**
       * The header lives in its own section. Re-rendering it through the Section
       * Rendering API would work but would also reset any open mega menu and the
       * logo swap that goes with it, so only the count is touched.
       *
       * The replacement text is READ OFF THE FETCHED SECTION rather than built
       * here. The drawer renders `header.cart_count` for the current count into a
       * hidden span, so the string stays in locales/en.default.json and there is
       * no pluralisation logic in JavaScript to drift from Liquid's.
       */
      updateHeaderCount(doc) {
        const label = doc.querySelector('[data-cart-count-label]');
        const count = doc.querySelector('[data-cart-count-value]');
        if (!label) return;

        const text = label.textContent.trim();
        const value = count ? count.textContent.trim() : '';

        document.querySelectorAll('[data-cart-count-text]').forEach((node) => {
          node.textContent = text;
        });

        /* The mobile link is an icon plus a bare numeral, and the numeral is
           absent at zero. Its accessible name carries the full sentence, which is
           why the numeral is aria-hidden in the markup. */
        document.querySelectorAll('[data-cart-toggle][aria-label]').forEach((node) => {
          node.setAttribute('aria-label', text);
        });

        document.querySelectorAll('[data-cart-count-number]').forEach((node) => {
          node.textContent = value;
          node.hidden = value === '' || value === '0';
        });
      }

      /**
       * Puts focus back on the control that was pressed. When the line has gone —
       * the customer decremented the last unit — focus moves to the close button,
       * which is the one control guaranteed to survive every render.
       */
      restoreFocus(focus) {
        if (!focus) return;

        const row = this.querySelector(`[data-cart-line][data-line="${focus.line}"]`);
        const target =
          row?.querySelector(`[data-cart-${focus.control}]:not([disabled])`) ||
          row?.querySelector('[data-cart-increase]') ||
          this.closeButton;

        target?.focus();
      }

      /**
       * Locale-aware root. On a multi-market shop the cart routes are prefixed
       * (/en-ca/cart/change.js) and a hardcoded path 404s, taking the drawer with
       * it. Shopify publishes the prefix on window.Shopify.routes.root.
       */
      static root() {
        return window.Shopify?.routes?.root || '/';
      }
    },
  );
}
